import React, { ReactNode, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowUpRight01Icon as ArrowUpRight, HelpCircleIcon as HelpCircle, InformationCircleIcon as Info, KeyboardIcon as Keyboard, LinkSquare01Icon as ExternalLink, Message01Icon as MessageCircle, MoreHorizontalIcon as MoreHorizontal, Plug01Icon as Plug, RefreshIcon as RefreshCw, SecurityCheckIcon as Shield } from '@hugeicons/core-free-icons';
import { useLocation, useNavigate, Outlet, Navigate } from "react-router-dom";
import { PageSkeleton } from "./PageSkeleton";
import { useIsFetching } from "@tanstack/react-query";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "./AppSidebar";
import { LaunchProgressBar } from "@/components/cloudos/LaunchProgressBar";
import { trackPageView } from "@/lib/track";
import { ActiveSessionBar } from "@/components/cloudos/ActiveSessionBar";
import { ChangelogPopup } from "@/components/changelog/ChangelogPopup";

// Page-level error boundary - shows error message instead of crashing
class ErrorBoundaryPage extends React.Component<{children: React.ReactNode}, {hasError: boolean, error: Error | null}> {
  state: {hasError: boolean, error: Error | null} = { hasError: false, error: null };
  static getDerivedStateFromError(error: Error) { return { hasError: true, error }; }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[AppLayout] Page crashed:', error.message, info.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-full p-8">
          <div className="text-center max-w-md">
            <h2 className="text-lg font-semibold text-foreground mb-2">Something went wrong</h2>
            <p className="text-sm text-muted-foreground mb-4">{this.state.error?.message || 'An unexpected error occurred'}</p>
            <button onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }} className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm">
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

import { TopBar } from "./TopBar";
import { RightPanel } from "./RightPanel";
import { CommandMenu } from "./CommandMenu";
import { SearchPopup } from "./SearchPopup";
import { SoftwareViewer } from "@/components/cloudos/SoftwareViewer";
import { PageTransition } from "./PageTransition";
import { useIsMobile } from "@/hooks/use-mobile";
import CONFIG from "@/config/env";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuthFetch } from "@/hooks/useAuthFetch";
import { useWorkspaceId } from "@/hooks/useWorkspaceId";
import { useLayoutMode } from "@/hooks/useLayout";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface AppLayoutProps {
  children?: ReactNode;
}

function AppLayoutContent({ children }: AppLayoutProps) {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);

  // Track page views
  useEffect(() => { trackPageView(); }, [location.pathname]);
  const [initialChromeLoading, setInitialChromeLoading] = useState(false);
  const [backendHealthChecking, setBackendHealthChecking] = useState(true);
  const [firstMountDone, setFirstMountDone] = useState(false);
  const isFetching = useIsFetching();
  const thresholdMs = 300;
  const contentTimerRef = useRef<number | null>(null);
  const chromeTimerRef = useRef<number | null>(null);
  const [routeGateActive, setRouteGateActive] = useState(false);
  const chromeDoneRef = useRef(false);
  const navigate = useNavigate();
  const authFetch = useAuthFetch();
  const workspaceId = useWorkspaceId();
  const [helpOpen, setHelpOpen] = useState(false);

  // Warm the git-connections cache once at app load (after auth) so the
  // Add-repository dialog + deployment pickers show the connected state instantly
  // on first open — permanently removes the "click, go back, come again" issue.
  useEffect(() => {
    if (localStorage.getItem('genofx:token')) {
      import('@/hooks/useGitConnections').then(m => m.prefetchGitConnections(authFetch)).catch(() => {});
    }
  }, [authFetch]);

  // Software Viewer state
  const [softwareSession, setSoftwareSession] = useState<{
    sessionId: string;
    streamUrl: string;
    softwareName: string;
  } | null>(null);

  // Listen for software launch events
  useEffect(() => {
    const handleLaunched = (e: Event) => {
      const { sessionId, streamUrl, softwareName } = (e as CustomEvent).detail;
      setSoftwareSession({ sessionId, streamUrl, softwareName });
    };
    window.addEventListener('GENOFX_SOFTWARE_LAUNCHED', handleLaunched);
    return () => window.removeEventListener('GENOFX_SOFTWARE_LAUNCHED', handleLaunched);
  }, []);

  // Extract token from URL params synchronously (needed for auth guard below)
  const urlToken = new URLSearchParams(window.location.search).get("token");

  // Fast backend health check on mount (2 second timeout)
  useEffect(() => {
    const healthCheck = async () => {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 2000);
        const healthUrl = `${CONFIG.API_BASE}/api/health`;
        await fetch(healthUrl, { signal: controller.signal });
        clearTimeout(timeout);
      } catch (e) {
        console.warn("[Auth] Backend unreachable or slow");
      } finally {
        setBackendHealthChecking(false);
        setFirstMountDone(true);
      }
    };
    healthCheck();
  }, []);

  useEffect(() => {
    // Persist URL token to localStorage and clean the URL
    if (urlToken) {
      localStorage.setItem("genofx:token", urlToken);
      const clean = new URL(window.location.href);
      clean.searchParams.delete("token");
      window.history.replaceState({}, document.title, clean.toString());
    }
  }, [urlToken]);

  useEffect(() => {
    setRouteGateActive(true);
    setLoading(false);
    if (contentTimerRef.current) {
      clearTimeout(contentTimerRef.current);
      contentTimerRef.current = null;
    }
  }, [location.pathname]);

  useEffect(() => {
    if (chromeDoneRef.current) {
      return;
    }
    if (isFetching > 0) {
      if (!chromeTimerRef.current) {
        chromeTimerRef.current = window.setTimeout(() => {
          setInitialChromeLoading(true);
          chromeTimerRef.current = null;
        }, thresholdMs);
      }
    } else {
      if (chromeTimerRef.current) {
        clearTimeout(chromeTimerRef.current);
        chromeTimerRef.current = null;
      }
      if (initialChromeLoading) {
        setInitialChromeLoading(false);
      }
      chromeDoneRef.current = true;
    }
  }, [isFetching, initialChromeLoading]);

  useEffect(() => {
    if (!routeGateActive) {
      return;
    }
    if (isFetching > 0) {
      if (!contentTimerRef.current) {
        contentTimerRef.current = window.setTimeout(() => {
          setLoading(true);
          contentTimerRef.current = null;
        }, thresholdMs);
      }
    } else {
      if (contentTimerRef.current) {
        clearTimeout(contentTimerRef.current);
        contentTimerRef.current = null;
      }
      setLoading(false);
      setRouteGateActive(false);
    }
  }, [isFetching, routeGateActive]);

  useEffect(() => {
    return () => {
      if (contentTimerRef.current) {
        clearTimeout(contentTimerRef.current);
      }
      if (chromeTimerRef.current) {
        clearTimeout(chromeTimerRef.current);
      }
    };
  }, []);

  // Check if we are on the dashboard (root path)
  const isDashboard = location.pathname === "/";

  // Check settings/inbox pages (no ads)
  const isSettingsPage =
    location.pathname.startsWith("/settings") ||
    location.pathname.startsWith("/inbox");
  // Check special layout pages
  // "Page Builder" layout (Canvas): /page/ or /frame/ (legacy path kept for compatibility)
  const isFrameBuilder = location.pathname.includes("/page/") || location.pathname.includes("/frame/") || location.pathname === "/page/builder" || location.pathname === "/frame/builder" || location.pathname === "/ui-builder";

  // All useMemo hooks declared before any early returns (Rules of Hooks)
  const isAppPage = useMemo(() => {
    return location.pathname.startsWith("/apps/") || location.pathname.startsWith("/plugins/automations");
  }, [location.pathname]);

  const appLabel = useMemo(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    const maybeApp = segments[1] || segments[0] || "App";
    return maybeApp.replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
  }, [location.pathname]);

  const appId = useMemo(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    return segments[1] || segments[0] || "app";
  }, [location.pathname]);

  // Auth guard — redirect to login when there is no token at all.
  // Must be AFTER all hooks, BEFORE any other early returns.
  const storedToken = localStorage.getItem("genofx:token");
  if (!urlToken && !storedToken) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    const loginUrl = `/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
    return <Navigate to={loginUrl} replace />;
  }

  // Modern gradient background — warm muted tones, fills entire surface (no black gaps)
  // Uses overlapping conic + radial gradients for a mesh effect
  // Noise applied via SVG feTurbulence composited with feBlend
  const bg = useMemo(() => {
    // Curated minimalist dark palettes (no neon, no pink/cyan)
    const palettes = [
      ['#1a1a2e', '#16213e', '#0f3460', '#1b2838'], // deep navy → ocean
      ['#1b2838', '#2c3e50', '#1a1a2e', '#1b4332'], // charcoal → forest
      ['#1a1a2e', '#533483', '#2c3e50', '#16213e'], // navy → deep purple
      ['#1b4332', '#2d6a4f', '#1b2838', '#16213e'], // forest → ocean
      ['#2c3e50', '#3d405b', '#1a1a2e', '#1b2838'], // slate → navy
      ['#16213e', '#1a1a2e', '#0f3460', '#2c3e50'], // midnight → charcoal
      ['#3d405b', '#1a1a2e', '#2c3e50', '#16213e'], // warm slate → navy
    ];
    const p = palettes[Math.floor(Math.random() * palettes.length)];
    const id = Math.random().toString(36).slice(2, 8);
    return {
      id,
      style: {
        background: `
          conic-gradient(from ${Math.floor(Math.random() * 360)}deg at 30% 20%, ${p[0]}, ${p[1]}, ${p[2]}, ${p[0]}),
          radial-gradient(ellipse 100% 80% at 70% 80%, ${p[2]}, ${p[3]}),
          radial-gradient(ellipse 80% 100% at 20% 60%, ${p[1]}, ${p[0]}),
          ${p[0]}
        `,
      },
      noiseId: `noise-${id}`,
    };
  }, []);

  // Check fixed height pages (Apps that manage their own scrolling)
  // Routes migrated from /plugins/* to /apps/* — keep both for backward-compat
  const isFixedHeightPage =
    location.pathname.startsWith("/inbox") ||
    location.pathname.startsWith("/plugins/calendar") ||
    location.pathname.startsWith("/apps/calendar") ||
    location.pathname.startsWith("/apps/webhooks") ||
    location.pathname.startsWith("/plugins/whiteboard") ||
    location.pathname.startsWith("/apps/whiteboard") ||
    isFrameBuilder;

  return (
    <div className={`flex w-full bg-transparent relative ${isFixedHeightPage ? "h-screen overflow-hidden" : "min-h-screen"}`}>
      {/* Global launch progress bar */}
      <LaunchProgressBar />

      {/* Background Layer: Random noise gradient — re-generates on layout mount (not per page nav) */}
      <div className="fixed inset-0 z-0 pointer-events-none" aria-hidden="true" style={bg.style}>
        {/* Grainy noise overlay — composited via SVG feBlend for visible grain on dark gradients */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.45 }}>
          <filter id={bg.noiseId} x="0%" y="0%" width="100%" height="100%">
            <feTurbulence type="fractalNoise" baseFrequency="0.75" numOctaves="4" stitchTiles="stitch" result="noise" />
            <feColorMatrix type="saturate" values="0" in="noise" result="grainBW" />
            <feBlend in="SourceGraphic" in2="grainBW" mode="overlay" />
          </filter>
          <rect width="100%" height="100%" filter={`url(#${bg.noiseId})`} fill="transparent" />
        </svg>
      </div>

      {/* Sidebar Wrapper - Sticky for document pages to stay visible while scrolling */}
      <div className={`relative z-20 ${!isFixedHeightPage ? "sticky top-0 h-screen" : ""}`}>
        <AppSidebar />
      </div>

      <div className={`flex flex-col flex-1 min-w-0 relative z-10 ${isFixedHeightPage ? "h-full overflow-hidden" : ""}`}>
        <ActiveSessionBar />
        <TopBar />
        <main className={`flex-1 relative min-h-0 flex flex-col ${!isFrameBuilder && !isFixedHeightPage ? "lg:p-6" : ""} ${isFixedHeightPage ? "overflow-hidden" : ""}`}>
            <div
              className={`${isFrameBuilder
                ? "w-full h-full p-2 flex flex-col"
                : `w-full max-w-[1600px] mx-auto flex flex-col ${isFixedHeightPage ? "flex-1 h-full min-h-0 overflow-hidden" : "min-h-[calc(100vh-theme(spacing.4))]"} bg-background shadow-sm lg:rounded-xl`
              }`}
              spellCheck={isFrameBuilder ? false : undefined}
              data-gramm={isFrameBuilder ? "false" : undefined}
              data-gramm_editor={isFrameBuilder ? "false" : undefined}
              autoCorrect={isFrameBuilder ? "off" : undefined}
              autoCapitalize={isFrameBuilder ? "off" : undefined}
              autoComplete={isFrameBuilder ? "off" : undefined}
            >
            {!isDashboard && !isFrameBuilder && isAppPage && (
              <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/60 bg-background/80 backdrop-blur-sm shrink-0 rounded-t-xl sticky top-0 z-10">
                <div className="text-xs font-medium text-muted-foreground">
                  {appLabel}
                </div>
                <div className="flex items-center gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-muted/60 hover:text-foreground data-[state=open]:bg-muted/60">
                        <HugeiconsIcon icon={MoreHorizontal} className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                      <DropdownMenuItem onClick={() => window.location.reload()}>
                        <HugeiconsIcon icon={RefreshCw} className="h-4 w-4 mr-2"  />
                        Refresh
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => toast.info("Permissions coming soon")}> 
                        <HugeiconsIcon icon={Shield} className="h-4 w-4 mr-2"  />
                        Permissions
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setHelpOpen(true)}> 
                        <HugeiconsIcon icon={HelpCircle} className="h-4 w-4 mr-2"  />
                        Help
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            )}

            <div className={`flex-1 min-h-0 ${isFixedHeightPage ? "h-full overflow-hidden" : "overflow-y-auto"}`}>
              <PageTransition isFixedHeight={isFixedHeightPage}>
                <ErrorBoundaryPage>
                  <Suspense fallback={<PageSkeleton />}>
                    {children ?? <Outlet />}
                  </Suspense>
                </ErrorBoundaryPage>
              </PageTransition>
            </div>
          </div>
        </main>
      </div>
      <RightPanel />
      <SearchPopup />

      {/* Software Viewer — full screen overlay when a software session is active */}
      {softwareSession && (
        <SoftwareViewer
          sessionId={softwareSession.sessionId}
          streamUrl={softwareSession.streamUrl}
          softwareName={softwareSession.softwareName}
          onClose={() => setSoftwareSession(null)}
        />
      )}

      {/* App Help Dialog */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HugeiconsIcon icon={HelpCircle} className="h-5 w-5 text-primary"  />
              {appLabel} — Help
            </DialogTitle>
            <DialogDescription>
              Quick reference for this app
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-start gap-3">
              <HugeiconsIcon icon={Keyboard} className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium">Keyboard Shortcuts</p>
                <div className="text-xs text-muted-foreground mt-1 space-y-1">
                  <p><kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">Ctrl+K</kbd> — Command palette</p>
                  <p><kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">Ctrl+/</kbd> — Toggle sidebar</p>
                  <p><kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">Ctrl+R</kbd> — Refresh app</p>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <HugeiconsIcon icon={Info} className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0"  />
              <div>
                <p className="text-sm font-medium">About this App</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Use the connectors button to link external services. Each app integrates with your workspace data and respects your role permissions.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <HugeiconsIcon icon={MessageCircle} className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0"  />
              <div>
                <p className="text-sm font-medium">Need more help?</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Visit <a href="https://genofx.com/docs" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">genofx.com/docs</a> or reach out through the community.
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Changelog Popup — shows once per user per new entry on login */}
      <ChangelogPopup />
    </div>
  );
}

export function AppLayout({ children }: AppLayoutProps) {
  return (
    <SidebarProvider defaultOpen={true}>
      <AppLayoutContent>{children}</AppLayoutContent>
    </SidebarProvider>
  );
}
